import React, { useEffect, useState } from 'react';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import { ResponsiveModal as Modal } from '@dealroadshow/uikit-modal';
import { buttonStyles } from '@dealroadshow/uikit-button';
import { IDataroomListItem } from '../../../../domain/vo/DataroomListItem';
import {
  STAGE_CONFIRM_BUTTON_LABEL,
  STAGE_MODAL_TITLES,
} from '../../AllowedActions/constants';
import useStages from './useStages';
import { DataroomStageName } from '@dataroom/entity/DataroomStageName';
import { Checkbox } from '@dealroadshow/uikit-checkbox';
import formatNumber from '@framework/data-helpers/formatters/formatNumber';

import styles from './moveStageModal.module.scss';

interface IProps {
  dataroom: IDataroomListItem,
  onClose: () => void,
}

const MoveStageModal = ({
  dataroom,
  onClose,
}: IProps) => {
  const [isChecked, setIsChecked] = useState(false);
  const nextStage = dataroom?.stage?.next;
  const isLiveNextStage = DataroomStageName.Live === nextStage;
  const isClosedNextStage = DataroomStageName.Closed === nextStage;

  const {
    isFetching,
    moveStage,
    getStageData,
    stageData: { investorsCount },
  } = useStages();

  useEffect(() => {
    isLiveNextStage && getStageData(dataroom?.id);
  }, []);

  if (!dataroom) {
    return null;
  }

  const handleMoveStage = () => {
    moveStage(dataroom, isLiveNextStage)
      .then(onClose);
  };

  const footer = (
    <div>
      <button
        type="submit"
        onClick={ handleMoveStage }
        className={ buttonStyles.action }
        data-test="ConfirmMoveStageButton"
        disabled={ isLiveNextStage && !isChecked }
      >
        { (STAGE_CONFIRM_BUTTON_LABEL as Record<string, (n: number) => string>)[nextStage](investorsCount || 0) }
      </button>
      <button
        type="button"
        onClick={ onClose }
        className={ buttonStyles.text }
        data-test="CancelMoveStageButton"
      >
        Cancel
      </button>
    </div>
  );

  return (
    <Modal
      onCloseClicked={ onClose }
      isVisible
      title={ (
        <h5>
          { (STAGE_MODAL_TITLES as Record<string, string>)[nextStage] }
        </h5>
      ) }
      footer={ footer }
      className={ styles.modal }
      onOverlayClicked={ onClose }
      dataTest="dataroomMoveStageModal"
    >
      <div className={ styles.modalContent }>
        <div className={ styles.note }>
          { isLiveNextStage ? (
            <>
              The “{ dataroom.displayName }” research will immediately go <strong>Live</strong> and invitation emails
              will be sent to all users in the Investor permission group. This action cannot be undone.
            </>
            )
            : (
              <>
                The “{ dataroom.displayName }” research will be
                immediately { isClosedNextStage ? '' : 'go' }
                <b> { isClosedNextStage ? 'Closed' : nextStage }</b>.
                This action is irreversible.
              </>
            ) }
        </div>
        { isLiveNextStage && (
          <Checkbox
            className={ styles.checkbox }
            dataTest="confirmToGoLive"
            checked={ isChecked }
            onChange={ (e) => setIsChecked(e.target.checked) }
            label={ `I confirm an email invitation will be sent to ${ investorsCount > 0 ? formatNumber(investorsCount) : '' } user(s)` }
          />
        ) }
      </div>
      <Spinner
        overlay
        isVisible={ isFetching }
      />
    </Modal>
  );
};

export default MoveStageModal;
